import type { QuickSearchCategoryView, QuickSearchView } from './quickSearchRoutes'
import type { QuickSearchResult } from '@/types/searchTypes'
import { VpButton, VpLoader } from '@vtmn-play/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useQuickSearch } from '@/hooks/useQuickSearch'
import { ANALYTICS_EVENTS, ANALYTICS_JOURNEY_CHANNELS, setPendingJourneyChannel, trackAnalyticsEvent } from '@/services/analytics'
import { getCategoryFromResultType, isValidQuickSearchQuery } from '@/utils/quickSearch'
import { QuickSearchChips } from './QuickSearchChips'
import { QuickSearchResults } from './QuickSearchResults'
import { buildQuickSearchResultLocation } from './quickSearchRoutes'
import './quick-search.scss'

interface QuickSearchPanelProps {
  query: string
  onClose: () => void
}

export function QuickSearchPanel({
  query,
  onClose,
}: Readonly<QuickSearchPanelProps>) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const exactSeasonMatch = useMatch('/:season')
  const deepSeasonMatch = useMatch('/:season/*')
  const currentSeason = (deepSeasonMatch ?? exactSeasonMatch)?.params?.season ?? ''
  const [selectedView, setSelectedView] = useState<QuickSearchView>('all')
  const normalizedQuery = query.trim()
  const { debouncedValue: debouncedQuery, isDebouncing } = useDebouncedValue(normalizedQuery, 350)
  const { data, isError, isLoading, isFetching, refetch } = useQuickSearch(debouncedQuery, currentSeason, { keepPreviousData: true })

  const displayedQuery = data?.query ?? debouncedQuery
  const totalResults = data && !isError ? data.total : 0
  const isInitialLoading = normalizedQuery.length > 0 && !data && (isLoading || isDebouncing)
  const isRefreshing = !!data && (isFetching || isDebouncing)

  const onResultSelect = async (result: QuickSearchResult) => {
    const category = getCategoryFromResultType(result.type)
    const categoryResults = data?.categories[category].results ?? []
    const rankPosition = categoryResults.findIndex(
      item => item.id === result.id && item.type === result.type,
    ) + 1

    trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_RESULT_CLICKED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'quick_search_modal',
      season: currentSeason,
      query: displayedQuery,
      query_length: displayedQuery.length,
      selected_view: selectedView,
      result_category: result.type,
      result_type: result.type,
      result_id: result.id,
      rank_position: rankPosition > 0 ? rankPosition : undefined,
      category_total: data?.categories[category].total,
      total_results: data?.total ?? 0,
    })

    setPendingJourneyChannel(ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH)
    await navigate(buildQuickSearchResultLocation(result, currentSeason))
    onClose()
  }

  const onSeeAll = async (view: QuickSearchCategoryView) => {
    if (!data) {
      return
    }

    trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_SEE_ALL_CLICKED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'quick_search_modal',
      season: currentSeason,
      query: data.query,
      query_length: data.query.length,
      selected_view: selectedView,
      target_view: view,
      category_total: data.categories[view].total,
      total_results: data.total,
    })

    await navigate(`/${currentSeason}/search?query=${encodeURIComponent(data.query)}&filter=${view}`)
    onClose()
  }

  const handleSelectView = (view: QuickSearchView) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_VIEW_CHANGED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'quick_search_modal',
      season: currentSeason,
      query: displayedQuery,
      query_length: displayedQuery.length,
      previous_view: selectedView,
      selected_view: view,
      category_total: view === 'all' || !data ? undefined : data.categories[view].total,
      total_results: data?.total ?? 0,
    })

    setSelectedView(view)
  }

  const renderContent = () => {
    if (normalizedQuery && !isValidQuickSearchQuery(normalizedQuery)) {
      return (
        <output className="quick-search__query-hint" data-testid="quick-search-query-length-hint">
          {t('quick-search.query-length-error')}
        </output>
      )
    }

    if (isError) {
      if (isDebouncing) {
        return (
          <div className="quick-search__loader" data-testid="quick-search-loader">
            <VpLoader />
          </div>
        )
      }

      return (
        <div className="quick-search__error" role="alert" data-testid="quick-search-error">
          <p>{t('quick-search.search-error')}</p>
          <VpButton variant="secondary" size="small" onClick={() => void refetch()}>
            {t('quick-search.retry-search')}
          </VpButton>
        </div>
      )
    }

    if (isInitialLoading) {
      return (
        <div className="quick-search__loader" data-testid="quick-search-loader">
          <VpLoader />
        </div>
      )
    }

    if (data) {
      return (
        <div className="quick-search__content">
          <QuickSearchResults
            query={displayedQuery}
            response={data}
            selectedView={selectedView}
            onResultSelect={onResultSelect}
            onSeeAll={onSeeAll}
          />
          {isRefreshing && (
            <div className="quick-search__loader quick-search__loader--overlay" data-testid="quick-search-refresh-loader">
              <VpLoader />
            </div>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div className="quick-search" data-testid="quick-search-panel">
      <p className="quick-search__count">{t('quick-search.results-label', { count: totalResults })}</p>
      <QuickSearchChips
        selectedView={selectedView}
        onSelectView={handleSelectView}
        response={isError ? undefined : data}
      />
      {renderContent()}
    </div>
  )
}
